import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import {
  LifeBuoy, Phone, MessageCircle, Globe, Heart, ShieldAlert, Users, AlertTriangle,
  ArrowLeft, Plus, Pencil, Trash2, Save, X, GripVertical,
} from 'lucide-react';

const ICON_KEYS = ['Phone', 'ShieldAlert', 'Users', 'Heart', 'Globe', 'LifeBuoy', 'MessageCircle', 'AlertTriangle'];
const ICONS = { LifeBuoy, Phone, MessageCircle, Globe, Heart, ShieldAlert, Users, AlertTriangle };
const TINT_PRESETS = ['#E57A44', '#8C5E5E', '#5B7C99', '#78A18E', '#B38A2E', '#B77BB0'];

export default function AdminResources() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showPageForm, setShowPageForm] = useState(false);
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeSectionId, setActiveSectionId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/resources');
      setData(res.data);
    } catch (e) {
      toast.error('Could not load resources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const savePage = async (payload) => {
    try {
      await api.put('/admin/resources/page', payload);
      toast.success('Page settings saved');
      setShowPageForm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const saveSection = async (payload, section_id) => {
    try {
      if (section_id) {
        await api.put(`/admin/resources/sections/${section_id}`, payload);
        toast.success('Section updated');
      } else {
        await api.post('/admin/resources/sections', payload);
        toast.success('Section added');
      }
      setShowSectionForm(false);
      setEditingSection(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const deleteSection = async (section) => {
    if (!window.confirm(`Delete "${section.title}" and all its resources? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/resources/sections/${section.id}`);
      toast.success('Section deleted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const saveItem = async (payload, item_id) => {
    try {
      if (item_id) {
        await api.put(`/admin/resources/items/${item_id}`, payload);
        toast.success('Resource updated');
      } else {
        await api.post('/admin/resources/items', payload);
        toast.success('Resource added');
      }
      setShowItemForm(false);
      setEditingItem(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(`/admin/resources/items/${item.id}`);
      toast.success('Resource deleted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const moveSection = async (idx, dir) => {
    const sections = [...data.sections];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= sections.length) return;
    [sections[idx], sections[newIdx]] = [sections[newIdx], sections[idx]];
    try {
      await api.post('/admin/resources/sections/reorder', { ids: sections.map(s => s.id) });
      await load();
    } catch (e) {
      toast.error('Reorder failed');
    }
  };

  const moveItem = async (section, idx, dir) => {
    const items = [...section.items];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    try {
      await api.post('/admin/resources/items/reorder', { ids: items.map(i => i.id) });
      await load();
    } catch (e) {
      toast.error('Reorder failed');
    }
  };

  if (loading || !data) {
    return (
      <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
        <Header />
        <div className="max-w-5xl mx-auto px-6 py-24 text-center" style={{ fontFamily: 'Lora, serif', color: '#8C867D' }}>Loading…</div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate('/admin')}
          className="mb-6 inline-flex items-center gap-2 text-sm hover:underline"
          style={{ color: '#5B7C99', fontFamily: 'Manrope, sans-serif' }}
          data-testid="admin-resources-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </button>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }} data-testid="admin-resources-title">Resources</h1>
            <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
              Edit the hero, emergency banner, and every crisis / support line on <code style={{ background: '#F3EFE7', padding: '2px 6px', borderRadius: 4 }}>/resources</code>.
            </p>
          </div>
          <a
            href="/resources"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded-full text-sm font-semibold transition hover:-translate-y-0.5"
            style={{ backgroundColor: '#F3EFE7', color: '#2C2724', border: '1px solid #E6E2D8', fontFamily: 'Manrope, sans-serif' }}
            data-testid="admin-resources-preview"
          >
            Preview live page
          </a>
        </div>

        {/* Page settings card */}
        <div className="p-6 rounded-3xl mb-8" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Hero & Emergency Banner</h2>
              <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Title, subtitle, badge label, and the amber alert box at the top of the page.</p>
            </div>
            <button
              onClick={() => setShowPageForm(true)}
              className="px-4 py-2 rounded-full text-sm font-semibold transition hover:-translate-y-0.5"
              style={{ backgroundColor: '#E57A44', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif' }}
              data-testid="admin-resources-edit-page"
            >
              <Pencil className="w-3 h-3 inline mr-1" /> Edit
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <PageField label="Badge label" value={data.page.hero_badge_label} />
            <PageField label="Hero title" value={data.page.hero_title} />
            <PageField label="Subtitle" value={data.page.hero_subtitle} span2 />
            <PageField label="Emergency title" value={data.page.emergency_title} />
            <PageField label="Emergency body" value={data.page.emergency_body} span2 />
          </div>
        </div>

        {/* Sections */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Sections & Resources</h2>
          <button
            onClick={() => { setEditingSection(null); setShowSectionForm(true); }}
            className="px-4 py-2 rounded-full text-sm font-semibold transition hover:-translate-y-0.5 inline-flex items-center gap-2"
            style={{ backgroundColor: '#78A18E', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif' }}
            data-testid="admin-resources-add-section"
          >
            <Plus className="w-4 h-4" /> New section
          </button>
        </div>

        <div className="space-y-6">
          {data.sections.map((section, sIdx) => {
            const IconComp = ICONS[section.icon_key] || Heart;
            return (
              <div key={section.id} className="rounded-3xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }} data-testid={`admin-section-${section.id}`}>
                <div className="p-5 flex items-center justify-between gap-3 flex-wrap" style={{ background: `${section.tint}12`, borderBottom: '1px solid #F0EBE0' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${section.tint}25`, color: section.tint }}>
                      <IconComp className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{section.title}</h3>
                      <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{section.items.length} resource{section.items.length === 1 ? '' : 's'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => moveSection(sIdx, -1)} disabled={sIdx === 0} className="p-2 rounded-full transition disabled:opacity-30" style={{ backgroundColor: '#F3EFE7' }} title="Move up">↑</button>
                    <button onClick={() => moveSection(sIdx, 1)} disabled={sIdx === data.sections.length - 1} className="p-2 rounded-full transition disabled:opacity-30" style={{ backgroundColor: '#F3EFE7' }} title="Move down">↓</button>
                    <button onClick={() => { setEditingSection(section); setShowSectionForm(true); }} className="p-2 rounded-full transition" style={{ backgroundColor: '#F3EFE7' }} title="Edit section" data-testid={`edit-section-${section.id}`}>
                      <Pencil className="w-4 h-4" style={{ color: '#5B7C99' }} />
                    </button>
                    <button onClick={() => deleteSection(section)} className="p-2 rounded-full transition" style={{ backgroundColor: '#FDECEB' }} title="Delete section" data-testid={`delete-section-${section.id}`}>
                      <Trash2 className="w-4 h-4" style={{ color: '#B8564A' }} />
                    </button>
                    <button
                      onClick={() => { setActiveSectionId(section.id); setEditingItem(null); setShowItemForm(true); }}
                      className="px-3 py-2 rounded-full text-xs font-semibold transition inline-flex items-center gap-1"
                      style={{ backgroundColor: section.tint, color: '#FFFFFF', fontFamily: 'Manrope, sans-serif' }}
                      data-testid={`add-item-${section.id}`}
                    >
                      <Plus className="w-3 h-3" /> Add resource
                    </button>
                  </div>
                </div>

                {section.items.length === 0 ? (
                  <div className="p-8 text-center text-sm italic" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                    No resources yet — add the first one above.
                  </div>
                ) : (
                  <div className="divide-y" style={{ borderColor: '#F0EBE0' }}>
                    {section.items.map((item, iIdx) => (
                      <div key={item.id} className="p-4 flex items-start justify-between gap-3 flex-wrap hover:bg-[#FDFBF7] transition" data-testid={`admin-item-${item.id}`}>
                        <div className="flex-1 min-w-[200px]">
                          <h4 className="font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{item.title}</h4>
                          <p className="text-xs mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.5 }}>{item.body}</p>
                          <div className="flex flex-wrap gap-1">
                            {(item.actions || []).map((a, i) => (
                              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F3EFE7', color: '#595553', fontFamily: 'Manrope, sans-serif' }}>
                                {a.label}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => moveItem(section, iIdx, -1)} disabled={iIdx === 0} className="p-1.5 rounded-full transition disabled:opacity-30 text-xs" style={{ backgroundColor: '#F3EFE7' }}>↑</button>
                          <button onClick={() => moveItem(section, iIdx, 1)} disabled={iIdx === section.items.length - 1} className="p-1.5 rounded-full transition disabled:opacity-30 text-xs" style={{ backgroundColor: '#F3EFE7' }}>↓</button>
                          <button onClick={() => { setActiveSectionId(section.id); setEditingItem(item); setShowItemForm(true); }} className="p-1.5 rounded-full transition" style={{ backgroundColor: '#F3EFE7' }} data-testid={`edit-item-${item.id}`}>
                            <Pencil className="w-3 h-3" style={{ color: '#5B7C99' }} />
                          </button>
                          <button onClick={() => deleteItem(item)} className="p-1.5 rounded-full transition" style={{ backgroundColor: '#FDECEB' }} data-testid={`delete-item-${item.id}`}>
                            <Trash2 className="w-3 h-3" style={{ color: '#B8564A' }} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showPageForm && <PageFormModal page={data.page} onClose={() => setShowPageForm(false)} onSave={savePage} />}
      {showSectionForm && (
        <SectionFormModal
          section={editingSection}
          onClose={() => { setShowSectionForm(false); setEditingSection(null); }}
          onSave={(p) => saveSection(p, editingSection?.id)}
        />
      )}
      {showItemForm && (
        <ItemFormModal
          item={editingItem}
          sectionId={activeSectionId || editingItem?.section_id}
          sections={data.sections}
          onClose={() => { setShowItemForm(false); setEditingItem(null); }}
          onSave={(p) => saveItem(p, editingItem?.id)}
        />
      )}

      <Footer />
    </div>
  );
}

function PageField({ label, value, span2 }) {
  return (
    <div className={span2 ? 'md:col-span-2' : ''}>
      <p className="text-xs uppercase tracking-wide mb-1" style={{ color: '#8C867D', fontWeight: 600 }}>{label}</p>
      <p style={{ color: '#2C2A29', lineHeight: 1.6 }}>{value}</p>
    </div>
  );
}

// ─── Modals ──────────────────────────────────────────────────────────────

function ModalShell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(44,42,41,0.4)' }} onClick={onClose} data-testid={testid}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl" style={{ backgroundColor: '#FDFBF7', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between p-5 border-b" style={{ backgroundColor: '#FDFBF7', borderColor: '#F0EBE0' }}>
          <h3 className="text-xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{title}</h3>
          <button onClick={onClose} className="p-2 rounded-full transition hover:bg-[#F3EFE7]" data-testid="modal-close">
            <X className="w-5 h-5" style={{ color: '#595553' }} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2A29' }}>{label}</label>
      {children}
      {hint && <p className="text-xs mt-1" style={{ color: '#8C867D', fontFamily: 'Nunito, sans-serif' }}>{hint}</p>}
    </div>
  );
}

const inputStyle = { fontFamily: 'Nunito, sans-serif', border: '1px solid #E6E2D8', borderRadius: 12, padding: '10px 14px', width: '100%', backgroundColor: '#FFFFFF', color: '#2C2A29' };

function PageFormModal({ page, onClose, onSave }) {
  const [form, setForm] = useState({
    hero_badge_label: page.hero_badge_label || '',
    hero_title: page.hero_title || '',
    hero_subtitle: page.hero_subtitle || '',
    emergency_title: page.emergency_title || '',
    emergency_body: page.emergency_body || '',
  });
  const submit = (e) => { e.preventDefault(); onSave(form); };
  return (
    <ModalShell title="Edit hero & emergency banner" onClose={onClose} testid="modal-page-form">
      <form onSubmit={submit}>
        <Field label="Badge label (small pill above the title)"><input style={inputStyle} value={form.hero_badge_label} onChange={(e) => setForm({ ...form, hero_badge_label: e.target.value })} data-testid="page-badge-label" /></Field>
        <Field label="Hero title"><input style={inputStyle} value={form.hero_title} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} required data-testid="page-hero-title" /></Field>
        <Field label="Subtitle (italic paragraph under the title)"><textarea style={{ ...inputStyle, minHeight: 100 }} value={form.hero_subtitle} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} required data-testid="page-subtitle" /></Field>
        <Field label="Emergency banner title"><input style={inputStyle} value={form.emergency_title} onChange={(e) => setForm({ ...form, emergency_title: e.target.value })} required data-testid="page-emerg-title" /></Field>
        <Field label="Emergency banner body"><textarea style={{ ...inputStyle, minHeight: 100 }} value={form.emergency_body} onChange={(e) => setForm({ ...form, emergency_body: e.target.value })} required data-testid="page-emerg-body" /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold" style={{ backgroundColor: '#F3EFE7', color: '#2C2724' }}>Cancel</button>
          <button type="submit" className="px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2" style={{ backgroundColor: '#E57A44', color: '#FFFFFF' }} data-testid="page-save-btn">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function SectionFormModal({ section, onClose, onSave }) {
  const [form, setForm] = useState({
    title: section?.title || '',
    icon_key: section?.icon_key || 'Heart',
    tint: section?.tint || '#78A18E',
  });
  const submit = (e) => { e.preventDefault(); onSave(form); };
  const IconPreview = ICONS[form.icon_key] || Heart;
  return (
    <ModalShell title={section ? 'Edit section' : 'New section'} onClose={onClose} testid="modal-section-form">
      <form onSubmit={submit}>
        <Field label="Section title"><input style={inputStyle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="section-title" /></Field>
        <Field label="Icon">
          <div className="flex flex-wrap gap-2">
            {ICON_KEYS.map((k) => {
              const I = ICONS[k];
              const active = form.icon_key === k;
              return (
                <button
                  key={k} type="button" onClick={() => setForm({ ...form, icon_key: k })}
                  className="p-3 rounded-2xl transition"
                  style={{ backgroundColor: active ? `${form.tint}25` : '#F3EFE7', border: active ? `2px solid ${form.tint}` : '2px solid transparent' }}
                >
                  <I className="w-5 h-5" style={{ color: active ? form.tint : '#595553' }} />
                </button>
              );
            })}
          </div>
        </Field>
        <Field label="Accent color">
          <div className="flex flex-wrap items-center gap-2">
            {TINT_PRESETS.map((t) => (
              <button key={t} type="button" onClick={() => setForm({ ...form, tint: t })} className="w-9 h-9 rounded-full transition" style={{ backgroundColor: t, border: form.tint === t ? '3px solid #2C2A29' : '3px solid #FDFBF7', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }} />
            ))}
            <input type="color" value={form.tint} onChange={(e) => setForm({ ...form, tint: e.target.value })} className="w-9 h-9 rounded-full cursor-pointer" style={{ border: '1px solid #E6E2D8' }} />
          </div>
        </Field>
        <div className="p-4 rounded-2xl mb-4 flex items-center gap-3" style={{ backgroundColor: `${form.tint}12`, border: '1px solid #F0EBE0' }}>
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${form.tint}25`, color: form.tint }}>
            <IconPreview className="w-6 h-6" />
          </div>
          <p className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{form.title || 'Preview'}</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold" style={{ backgroundColor: '#F3EFE7', color: '#2C2724' }}>Cancel</button>
          <button type="submit" className="px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2" style={{ backgroundColor: form.tint, color: '#FFFFFF' }} data-testid="section-save-btn">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ItemFormModal({ item, sectionId, sections, onClose, onSave }) {
  const [form, setForm] = useState({
    section_id: sectionId || item?.section_id || sections[0]?.id || '',
    title: item?.title || '',
    body: item?.body || '',
    actions: (item?.actions && item.actions.length > 0) ? item.actions : [{ label: '', href: '' }],
  });
  const addAction = () => setForm({ ...form, actions: [...form.actions, { label: '', href: '' }] });
  const setAction = (i, field, val) => {
    const next = [...form.actions];
    next[i] = { ...next[i], [field]: val };
    setForm({ ...form, actions: next });
  };
  const removeAction = (i) => setForm({ ...form, actions: form.actions.filter((_, idx) => idx !== i) });
  const submit = (e) => {
    e.preventDefault();
    const cleanActions = form.actions.filter(a => a.label.trim() && a.href.trim());
    onSave({ ...form, actions: cleanActions });
  };
  return (
    <ModalShell title={item ? 'Edit resource' : 'New resource'} onClose={onClose} testid="modal-item-form">
      <form onSubmit={submit}>
        <Field label="Section">
          <select style={inputStyle} value={form.section_id} onChange={(e) => setForm({ ...form, section_id: e.target.value })} required data-testid="item-section">
            {sections.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
        </Field>
        <Field label="Title (e.g. 988 Suicide & Crisis Lifeline)"><input style={inputStyle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="item-title" /></Field>
        <Field label="Description"><textarea style={{ ...inputStyle, minHeight: 100 }} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required data-testid="item-body" /></Field>
        <Field label="Call-to-action buttons" hint="Use tel:XXXXXXXXXX for phone, sms:XXXXX for text, https://... for websites. The first button is highlighted.">
          {form.actions.map((a, i) => (
            <div key={i} className="flex gap-2 mb-2 flex-wrap">
              <input style={{ ...inputStyle, flex: '1 1 140px' }} placeholder="Label (e.g. Call 988)" value={a.label} onChange={(e) => setAction(i, 'label', e.target.value)} data-testid={`item-action-label-${i}`} />
              <input style={{ ...inputStyle, flex: '2 1 240px' }} placeholder="Link (tel:988 or https://...)" value={a.href} onChange={(e) => setAction(i, 'href', e.target.value)} data-testid={`item-action-href-${i}`} />
              <button type="button" onClick={() => removeAction(i)} className="px-2 rounded-full" style={{ backgroundColor: '#FDECEB', color: '#B8564A' }} title="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={addAction} className="text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1" style={{ backgroundColor: '#F3EFE7', color: '#2C2724', fontFamily: 'Manrope, sans-serif' }} data-testid="add-action-btn">
            <Plus className="w-3 h-3" /> Add button
          </button>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold" style={{ backgroundColor: '#F3EFE7', color: '#2C2724' }}>Cancel</button>
          <button type="submit" className="px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2" style={{ backgroundColor: '#E57A44', color: '#FFFFFF' }} data-testid="item-save-btn">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
